import { uuid } from '@pp/toolkit';

import { snakeToCamel, type CamelCaseKeys } from '../api/converters/snakeToCamel';
import { LocalStorage } from '../storage/local/LocalStorage';
import { SessionStorage } from '../storage/session/SessionStorage';
import type { AllowedGlobals } from '../utils/window';

export const defaultRawSession: IUserSessionRaw = {
  JSESSIONID: '',
  actual_web_server: '',
  casino_id: '',
  config_url: '',
  game_mode: '',
  gametype: '',
  hasQuickGuide: '',
  hostCommunicationVersion: '',
  lang: '',
  meta_server: '',
  operator_theme: '',
  socket_port: '',
  socket_server: '',
  stats_collector_uuid: '',
  table_id: '',
  token: '',
  uiversion: '',
  web_server: '',
  lobbyFilter: '',
  gamePath: '',
};

export interface IUserSessionRaw extends Record<string, string> {
  JSESSIONID: string;
  actual_web_server: string;
  casino_id: string;
  config_url: string;
  game_mode: string;
  gametype: string;
  hasQuickGuide: string;
  hostCommunicationVersion: string;
  lang: string;
  meta_server: string;
  operator_theme: string;
  socket_port: string;
  socket_server: string;
  stats_collector_uuid: string;
  table_id: string;
  token: string;
  uiversion: string;
  web_server: string;
  lobbyFilter: string;
  gamePath: string;
}

export interface IUserSession extends CamelCaseKeys<IUserSessionRaw>, Record<string, string> {}

const isProduction = process.env.NODE_ENV === 'production';

export class UserSession {
  static session?: IUserSession | null;
  static sessionKey = window.name === 'lobby_iframe' ? 'PPG_L' : 'PPG'; // TODO: VERY SAD, IS BAD, NEEDS TO BE FIXED
  static coreSessionIdKey = 'coreSessionId';
  static coreBrowserIdKey = 'coreBrowserId';
  static coreSessionId: string | null;
  static coreBrowserId: string | null | undefined;

  private static parseUrlSession(): IUserSessionRaw | null {
    const windowObject = (window as AllowedGlobals).legacyMode ? window.parent : window;
    if (!windowObject.location.search) return null;
    const sessionResult: IUserSessionRaw = { ...defaultRawSession };
    const searchParams = new URLSearchParams(windowObject.location.search);
    searchParams.forEach((value, key) => {
      sessionResult[key] = value === 'null' ? '' : value;
    });
    sessionResult.gamePath = windowObject.location.pathname.replace(/\/+$/, '');
    return sessionResult;
  }

  private static postProcessSession(session: IUserSessionRaw): IUserSession {
    const processedSession = { ...session };

    if (processedSession && !processedSession.meta_server) {
      processedSession.meta_server = processedSession.actual_web_server;
    }

    if (!isProduction && processedSession) {
      const urlsToPrefix = ['actual_web_server', 'web_server', 'meta_server'];
      urlsToPrefix.forEach((url) => {
        const processedSessionElement = processedSession[url];
        if (!processedSessionElement?.includes('proxy')) {
          processedSession[url] = '/proxy/' + processedSessionElement;
        }
      });
    }

    return snakeToCamel(processedSession);
  }

  private static getStoredCoreSessionId(): string | null {
    const storedSession = SessionStorage.get(UserSession.coreSessionIdKey);
    return storedSession ?? null;
  }

  private static getURLSession(): IUserSessionRaw | null {
    const storedSession = UserSession.parseUrlSession();
    return storedSession ?? null;
  }

  private static getHistorySession(): IUserSessionRaw | null {
    const windowObject = (window as AllowedGlobals).legacyMode ? window.parent : window;
    const storedSession = windowObject.history.state;
    if (!storedSession?.JSESSIONID) {
      return null;
    }
    return storedSession;
  }

  private static getStoredSession(): IUserSessionRaw | null {
    const storedSession = SessionStorage.getJson<IUserSessionRaw>(UserSession.sessionKey);
    return storedSession ?? null;
  }

  public static toURLSearchParams(): URLSearchParams {
    return new URLSearchParams(UserSession.session ?? {});
  }

  public static init(): void {
    const rawSession = UserSession.getURLSession() ?? UserSession.getHistorySession() ?? UserSession.getStoredSession();
    if (rawSession?.vendor !== 'slots' && !rawSession?.JSESSIONID) {
      UserSession.session = null;
    } else {
      const windowObject = (window as AllowedGlobals).legacyMode ? window.parent : window;
      const currentPath = windowObject.location.pathname.replace(/\/+$/, '');
      if (!currentPath.includes('lobby') && currentPath !== rawSession.gamePath) {
        return windowObject.history.back();
      }
      UserSession.session = UserSession.postProcessSession(rawSession);
    }

    UserSession.coreSessionId = UserSession.getStoredCoreSessionId();
    if (!UserSession.coreSessionId) {
      UserSession.coreSessionId = uuid.v4();
      SessionStorage.set(UserSession.coreSessionIdKey, UserSession.coreSessionId);
    }

    UserSession.coreBrowserId = LocalStorage.get(UserSession.coreBrowserIdKey);
    if (!UserSession.coreBrowserId) {
      UserSession.coreBrowserId = uuid.v4();
      LocalStorage.set(UserSession.coreBrowserIdKey, UserSession.coreBrowserId);
    }
  }

  public static clearUrlSession(): void {
    const windowObject = (window as AllowedGlobals).legacyMode ? window.parent : window;
    if (windowObject.location.search) {
      const session = UserSession.getURLSession();
      if (session && !session.preserveUrlSession) {
        SessionStorage.setJson(UserSession.sessionKey, session);
        windowObject.history.replaceState(session, '', windowObject.location.pathname);
      }
    }
  }

  public static getSession(): IUserSession | null {
    /** We check if session already had attempted initialisation, and only if it has not, we initialise it */
    if (UserSession.session === undefined) {
      UserSession.init();
    }
    return UserSession.session || null;
  }

  public static getCoreSessionId(): string | null {
    return UserSession.coreSessionId;
  }

  public static getCoreBrowserId(): string | null | undefined {
    return UserSession.coreBrowserId;
  }
}
